import { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Context } from "../../context";
import { getCurrentUser } from "../../services/login";
import {
    Button,
    Label,
    makeStyles,
    shorthands,
    tokens,
} from "@fluentui/react-components";
import {
    cvtTaskStatus,
    idString,
} from "../../db_utils";
import {
    NextFilled,
    EyeRegular,
    PlayRegular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
    root: {
        height: "100%",
        overflowY: "auto",
        display: "grid",
        gridTemplateColumns: "2fr 7fr",
        ...shorthands.flex(1),
        ...shorthands.padding("10px"),
        ...shorthands.gap("10px"),
    },
    list: {
        display: "flex",
        overflowY: "auto",
        flexDirection: "column",
        backgroundColor: "#eee",
        ...shorthands.flex(1),
        ...shorthands.padding("10px"),
        ...shorthands.gap("10px"),
    },
    content: {
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        ...shorthands.flex(4),
        ...shorthands.padding("10px"),
        ...shorthands.gap("10px"),
    },
    card: {
        display: "flex",
        backgroundColor: "white",
        flexDirection: "column",
        boxShadow: tokens.shadow4,
        ...shorthands.padding("10px"),
        ...shorthands.gap("5px"),
        ...shorthands.margin("10px"),
    },
});

const isRootOrSessionTask = (task) => {
    return !!(
        task?.isSession ||
        task?.seq?.endsWith("-00") ||
        (task?.name || "").endsWith(" - ROOT")
    );
};

const normalizeStatus = (status) => (status || "").toString().toLowerCase();

const isCompletedAnnotationStatus = (status) => {
    return ["finished", "completed", "done"].includes(normalizeStatus(status));
};

const TaskCard = ({ task, onClick, curTask, doRefresh }) => {
    const navigate = useNavigate();

    let styles = useStyles();
    return (
        <div
            className={styles.card}
            style={{
                backgroundColor:
                    idString(curTask?.id) == idString(task.id)
                        ? tokens.colorBrandBackground2
                        : "white",
            }}
        >
            <h3>{`编号:${task.seq}`}</h3>
            <div
                style={{
                    flex: 1,
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr",
                    gap: 10,
                }}
            >
                <Label>任务名</Label>
                <Label>{task.name}</Label>
                <Label>当前状态</Label>
                <Label>{cvtTaskStatus(task.status)}</Label>
                <Label>任务截止</Label>
                <Label>
                    {new Date(task.completed_at).toLocaleDateString()}
                </Label>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
                <Button onClick={onClick} icon={<EyeRegular />}>
                    查看
                </Button>
                <Button icon={<NextFilled />} onClick={() => navigate(`/task/annotation?sessionId=${task.sessionId}`)}>
                    详情
                </Button>
            </div>
        </div>
    );
};

const TaskList = ({ tasks, onSelect, doRefresh }) => {
    let [curTask, setCurTask] = useState(null);
    let styles = useStyles();
    return (
        <div className={styles.list}>
            <h3
                style={{
                    background: "white",
                    padding: 10,
                    boxShadow: tokens.shadow8,
                }}
            >
                后台处理任务列表: 共计 {tasks?.length || 0} 任务
            </h3>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    overflowY: "auto",
                }}
            >
                {(tasks || []).map((t, i) => (
                    <TaskCard
                        doRefresh={doRefresh}
                        curTask={curTask}
                        task={t}
                        key={i}
                        onClick={() => {
                            setCurTask(t);
                            onSelect(t);
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

const TaskAnnotationContent = ({ task, doRefresh }) => {
    let { apiClient } = useContext(Context);
    let styles = useStyles();
    const navigate = useNavigate();

    const startLabelUAnnotation = async () => {
        try {
            const mediaMode = (process.env.REACT_APP_MEDIA_MODE || 'oss').toLowerCase() === 'media';
            // 1) Ensure /media symlinks exist only in media mode
            if (mediaMode) {
                try {
                    await apiClient.createMediaSymlinks(task.id);
                } catch (e) {
                    console.warn('Symlink creation failed (continuing):', e);
                }
            }

            // 2) Create LabelU annotation task
            // Call API to create LabelU annotation task
            const result = await apiClient.post(`/labelu/create-annotation-task/${task.id}`, {
                taskId: task.id,
                taskName: task.name,
                taskSeq: task.seq
            });

            alert(`LabelU标注任务已创建: ${result.message}`);
            // Open LabelU in new tab
            if (result.labeluUrl) {
                window.open(result.labeluUrl, '_blank');
            }
            doRefresh();
        } catch (error) {
            console.error('Failed to create LabelU annotation task:', error);
            alert('创建标注任务失败: ' + (error.message || error));
        }
    };

    const isRootTask = isRootOrSessionTask(task);

    // Auto-refresh per‑turbine annotation statuses when viewing a session (root) task
    useEffect(() => {
        if (!task || !isRootTask) return;
        const timer = setInterval(async () => {
            try {
                const withTasks = (task.turbines || []).filter(t => t.annotation_task_id);
                for (const t of withTasks) {
                    await apiClient.get(`/labelu/check-status/${t.annotation_task_id}`);
                }
                // Trigger a reload upstream
                doRefresh();
            } catch (err) {
                // Silently ignore refresh errors
                console.debug('annotation auto-refresh error', err);
            }
        }, 30000); // 30s interval
        return () => clearInterval(timer);
    }, [task, isRootTask]);

    if (!task) {
        return (
            <div className={styles.content}>
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    fontSize: 18,
                    color: "#666"
                }}>
                    请选择要标注的任务
                </div>
            </div>
        );
    }

    return (
        <div className={styles.content}>
            <h3>后台处理任务: {task.name}</h3>
            <div style={{
                backgroundColor: "white",
                padding: 20,
                borderRadius: 8,
                boxShadow: tokens.shadow4
            }}>
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 15,
                    marginBottom: 20
                }}>
                    <div>
                        <Label>任务编号:</Label>
                        <div>{task.seq}</div>
                    </div>
                    <div>
                        <Label>任务名称:</Label>
                        <div>{task.name}</div>
                    </div>
                    <div>
                        <Label>当前状态:</Label>
                        <div>{cvtTaskStatus(task.status)}</div>
                    </div>
                    <div>
                        <Label>创建时间:</Label>
                        <div>{new Date(task.created_at).toLocaleString()}</div>
                    </div>
                </div>
                
                {isRootTask && (
                    <div style={{
                        marginTop: 20,
                        backgroundColor: '#f6f6f6',
                        padding: 10,
                        borderRadius: 6
                    }}>
                        <h4>风机标注进度</h4>
                        {/* Session-level recheck entry point: only after all turbines finished */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <Button
                                appearance="primary"
                                size="small"
                                disabled={(task.session?.status || '').toLowerCase() !== 'completed'}
                                onClick={() => navigate(`/task/recheck?sessionId=${task.sessionId}`)}
                                title={((task.session?.status || '').toLowerCase() !== 'completed') ? '需所有风机标注完成后方可复核' : ''}
                            >
                                开始复核
                            </Button>
                        </div>
                        <table style={{ width: '100%', fontSize: 14 }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>风机编号</th>
                                    <th style={{ textAlign: 'left' }}>风机名称</th>
                                    <th style={{ textAlign: 'left' }}>状态</th>
                                    <th style={{ textAlign: 'left' }}>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                            {(task.turbines || []).map(t => {
                                // Only trust explicit annotation_status from backend
                                const status = t.annotation_status || 'waiting';
                                const normalizedStatus = normalizeStatus(status);
                                const isCompleted = isCompletedAnnotationStatus(status);
                                const isInRecheck = normalizedStatus === 'recheck';
                                const isInAnnotation = normalizedStatus === 'annotation';
                                const ready = !!t.ready_for_annotation;
                                const text = isInRecheck
                                    ? '待复核'
                                    : (isCompleted ? '已完成' : (isInAnnotation ? '标注中' : (ready ? '可开始' : '未开始')));

                                const handleStartForTurbine = async () => {
                                    try {
                                        let turbineTaskId = t.annotation_task_id; // may be null if not created yet
                                        // Create per‑turbine task if missing
                                        if (!turbineTaskId) {
                                            const created = await apiClient.createSessionTurbineTask(task.sessionId, t.id);
                                            turbineTaskId = created?.id || null;
                                        }
                                        if (!turbineTaskId) {
                                            alert('无法创建风机任务');
                                            return;
                                        }
                                        // Ensure media symlinks
                                        try { await apiClient.createMediaSymlinks(turbineTaskId); } catch {}
                                        // Create/ensure LabelU task
                                        const result = await apiClient.post(`/labelu/create-annotation-task/${turbineTaskId}`, {});
                                        alert(`LabelU标注任务已创建: ${result.message || ''}`);
                                        if (result.labeluUrl) window.open(result.labeluUrl, '_blank');
                                        doRefresh();
                                    } catch (err) {
                                        console.error('start turbine annotation failed', err);
                                        alert('开始标注失败: ' + (err.message || err));
                                    }
                                };

                                const openLabelU = async () => {
                                    try {
                                        if (!t.annotation_task_id) return;
                                        const j = await apiClient.get(`/labelu/task-url/${t.annotation_task_id}`);
                                        if (j.labeluUrl) {
                                            window.open(j.labeluUrl, '_blank');
                                        } else {
                                            alert('无法获取标注链接');
                                        }
                                    } catch (e) {
                                        console.error('open labelu failed', e);
                                        alert('打开标注失败');
                                    }
                                };

                                const openRecheck = () => {
                                    if (!t.annotation_task_id) return;
                                    navigate(`/task/recheck?view=tasks&sessionId=${task.sessionId}&taskId=${t.annotation_task_id}`);
                                };

                                const submitTurbineRecheck = async () => {
                                    try {
                                        if (!t.annotation_task_id) {
                                            alert('缺少风机标注任务');
                                            return;
                                        }

                                        await apiClient.updateTask(t.annotation_task_id, {
                                            Status: 'recheck',
                                            UpdatedAt: new Date(),
                                        });

                                        alert('已提交复核');
                                        doRefresh();
                                        openRecheck();
                                    } catch (err) {
                                        console.error('submit turbine recheck failed', err);
                                        alert('提交复核失败: ' + (err.message || err));
                                    }
                                };

                                const actionArea = () => {
                                    // Grey the start button once annotation has started; show open + disabled primary
                                    if (t.annotation_task_id && isInAnnotation) {
                                        return (
                                            <Fragment>
                                                <Button size="small" appearance="secondary" onClick={openLabelU}>打开</Button>
                                                <Button size="small" appearance="primary" disabled>
                                                    标注中
                                                </Button>
                                            </Fragment>
                                        );
                                    }
                                    if (t.annotation_task_id && isInRecheck) {
                                        return (
                                            <Fragment>
                                                <Button size="small" appearance="secondary" onClick={openLabelU}>打开</Button>
                                                <Button size="small" appearance="primary" onClick={openRecheck}>复核</Button>
                                            </Fragment>
                                        );
                                    }

                                    if (t.annotation_task_id && isCompleted) {
                                        return (
                                            <Fragment>
                                                <Button size="small" appearance="secondary" onClick={openLabelU}>打开</Button>
                                                <Button size="small" appearance="primary" onClick={submitTurbineRecheck}>提交复核</Button>
                                            </Fragment>
                                        );
                                    }
                                    // Default: allow starting annotation when ready; otherwise show disabled
                                    return (
                                        <Button size="small" appearance="primary" onClick={handleStartForTurbine} disabled={!ready && !t.annotation_task_id}>
                                            {t.annotation_task_id ? '打开/开始标注' : (ready ? '开始标注' : '待上传')}
                                        </Button>
                                    );
                                };

                                return (
                                    <tr key={t.id}>
                                        <td>{t.seq}</td>
                                        <td>{t.name}</td>
                                        <td>{text}</td>
                                        <td>
                                            {actionArea()}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div style={{
                    backgroundColor: "#f8f9fa",
                    padding: 15,
                    borderRadius: 6,
                    marginTop: 20
                }}>
                    <h4>标注说明</h4>
                    <p>1. 此任务的图像数据已完成上传处理</p>
                    <p>2. 点击"开始标注"按钮创建LabelU标注任务</p>
                    <p>3. 在LabelU系统中完成图像标注工作</p>
                    <p>4. 标注完成后点击"提交复核"按钮</p>
                    <p>5. 任务将自动流转到复核阶段</p>
                </div>
                
                {!isRootTask && (
                    <div style={{
                        marginTop: 20,
                        display: "flex",
                        gap: 10,
                        justifyContent: "center"
                    }}>
                        <Button 
                            appearance="primary" 
                            icon={<PlayRegular />}
                            onClick={startLabelUAnnotation}
                        >
                            开始标注 (LabelU)
                        </Button>
                    </div>
                )}

                {isRootTask && (
                    <div style={{
                        marginTop: 12,
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}>
                        <Button
                            appearance="secondary"
                            size="small"
                            onClick={async () => {
                                try {
                                    // For each turbine that has an annotation task, ping backend to refresh status
                                    const withTasks = (task.turbines || []).filter(t => t.annotation_task_id);
                                    for (const t of withTasks) {
                                        await apiClient.get(`/labelu/check-status/${t.annotation_task_id}`);
                                    }
                                    alert('状态已刷新');
                                    doRefresh();
                                } catch (err) {
                                    console.error('刷新标注状态失败', err);
                                    alert('刷新标注状态失败');
                                }
                            }}
                        >
                            刷新状态
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export const TaskAnnotation = () => {
    let { apiClient, currentField } = useContext(Context);
    let user = getCurrentUser();
    let [tasks, setTasks] = useState(null);
    let [task, setTask] = useState(null);
    let [refresh, setRefresh] = useState(0);
    let [searchParams, setSearchParams] = useSearchParams();

    const doRefresh = () => {
        setRefresh(refresh + 1);
    };

    const listTask = async () => {
        try {
            // Get sessions that are still in backend processing (not all tasks completed)
            const sessions = await apiClient.get('/inspectionsession?reviewStatus=backend_processing');

            // Fetch the real single-turbine tasks and group them for session-level progress.
            const params = {
                status: 'annotation'
            };
            if (user.role?.toLowerCase() !== "admin" && user.role?.toLowerCase() !== "field_admin") {
                // Add any user-specific filtering if needed
            }
            // Get current field from Context
            if (currentField && currentField.id) {
                params.fieldId = currentField.id;
            }
            const tasks = await apiClient.getTasks(params);
            const tasksBySessionId = new Map();
            for (const task of (tasks || [])) {
                if (!task.sessionId) continue;
                const key = String(task.sessionId);
                const rows = tasksBySessionId.get(key) || [];
                rows.push(task);
                tasksBySessionId.set(key, rows);
            }

            // Combine sessions and individual tasks
            // Convert sessions to task-like format for display
            const sessionTasks = (sessions || []).map(s => {
                const sessionTaskRows = tasksBySessionId.get(String(s.id)) || [];
                const representativeTask = sessionTaskRows[0];

                if (representativeTask) {
                    const turbinesById = new Map();
                    for (const row of sessionTaskRows) {
                        for (const turbine of (row.turbines || [])) {
                            turbinesById.set(String(turbine.id), turbine);
                        }
                    }
                    return {
                        ...representativeTask,
                        seq: s.seq || representativeTask.seq,
                        name: s.sessionName || representativeTask.name,
                        status: 'annotation',
                        sessionId: s.id,
                        created_at: representativeTask.created_at || s.createdAt,
                        completed_at: representativeTask.completed_at || s.createdAt,
                        isSession: true,
                        taskCount: s.taskCount,
                        turbines: [...turbinesById.values()],
                        session: {
                            ...(representativeTask.session || {}),
                            id: s.id,
                            seq: s.seq,
                            sessionName: s.sessionName,
                            status: s.status,
                            totalTurbines: s.totalTurbines,
                            completedTurbines: s.completedTurbines,
                        },
                    };
                }

                return {
                    id: s.id,
                    seq: s.seq,
                    name: s.sessionName,
                    status: 'annotation',
                    sessionId: s.id,
                    created_at: s.createdAt,
                    completed_at: s.createdAt, // Use createdAt as fallback
                    isSession: true,
                    taskCount: s.taskCount,
                    turbines: [],
                };
            });

            // Merge session tasks with individual tasks, avoiding duplicates
            const allTasks = [...sessionTasks];
            for (const task of (tasks || [])) {
                if (!task.sessionId || !sessionTasks.find(s => String(s.sessionId) === String(task.sessionId))) {
                    allTasks.push(task);
                }
            }

            setTasks(allTasks);
        } catch (error) {
            console.error('Failed to load annotation tasks:', error);
            setTasks([]);
        }
    };

    useEffect(() => {
        if (apiClient) listTask();
    }, [apiClient, refresh, currentField]);

    // Auto-select session if sessionId appears in query string
    useEffect(() => {
        if (!tasks || tasks.length === 0) return;
        const sid = searchParams.get('sessionId');
        if (!sid) return;
        const t = tasks.find(x => String(x.sessionId) === String(sid));
        if (t) setTask(t);
    }, [tasks, searchParams]);

    let styles = useStyles();
    return (
        <div className={styles.root}>
            <TaskList
                tasks={tasks}
                onSelect={(task) => {
                    setTask(task);
                }}
                doRefresh={doRefresh}
            />
            <TaskAnnotationContent task={task} doRefresh={doRefresh} />
        </div>
    );
};
